/**
 * 0.46.82 - The public site's login page, as designed in Pages and
 * forms (page type Login page, services/login_page.py on the server).
 *
 * The page's built-in elements carry `builtin: '<key>'` and are wired
 * here, by that key (not by their name or type, which the admin may
 * change): the email and password fields feed the sign-in, Log in
 * submits, the message box shows what went wrong, the ID card button
 * goes to the card address (and is left out where ID cards are off).
 * Everything else on the page is the admin's and renders as anywhere.
 *
 * If the page cannot be loaded, the fixed Henaket login (Login.jsx) is
 * shown instead, so nobody is ever without a way to sign in. The admin
 * panel always uses that fixed one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import PageContentRenderer from '../components/PageContentRenderer';
import { buttonTemplatesPublicApi } from '../api/admin';
import { fetchJson } from '../api/pages';
import { tabTitle } from '../lib/systemIdentity';
import Login from './Login';

function builtinNames(structure) {
  const out = {};
  const walk = (items) => (items || []).forEach((el) => {
    if (el && el.builtin && (el.element_id || el.name)) out[el.builtin] = el.element_id || el.name;
    if (el && Array.isArray(el.children)) walk(el.children);
  });
  walk(structure);
  return out;
}

/* 0.49.29 - afterLogin: where to go after signing in when no ?next= says
   otherwise. The home page uses it when it shows this page in its place
   ("/": the home page itself, now signed in). */
export default function LoginPage({ afterLogin } = {}) {
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [page, setPage] = useState(null);
  const [failed, setFailed] = useState(false);
  const [buttonTemplates, setButtonTemplates] = useState(() => buttonTemplatesPublicApi.cached() || []);
  const [values, setValues] = useState({});
  const [error, setError] = useState('');
  const [blockedMethod, setBlockedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idCardAvailable, setIdCardAvailable] = useState(false);
  const [cardPort, setCardPort] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchJson(`/api/pages/login-page?${new URLSearchParams({ lang: language })}`)
      .then((d) => { if (!cancelled) { setPage(d); setFailed(false); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [language]);

  useEffect(() => {
    buttonTemplatesPublicApi.list()
      .then((d) => setButtonTemplates(Array.isArray(d) ? d : []))
      .catch(() => setButtonTemplates([]));
    fetch('/api/auth/id-card/status', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { available: false }))
      .then((d) => { setIdCardAvailable(!!d.available); setCardPort(d.card_port || null); })
      .catch(() => setIdCardAvailable(false));
  }, []);

  useEffect(() => {
    if (page) document.title = tabTitle(page.title || t('login.title'));
  }, [page, t]);

  const names = useMemo(() => builtinNames(page && page.structure), [page]);

  const submit = useCallback(async () => {
    if (loading) return;
    setError('');
    setBlockedMethod(null);
    setLoading(true);
    const email = String(values[names.login_email] || '').trim();
    const password = String(values[names.login_password] || '');
    try {
      const result = await login(email, password);
      if (result && result.login_method_blocked) {
        setBlockedMethod(result.allowed_method);
        return;
      }
      const nextParam = new URLSearchParams(location.search).get('next');
      const safeNext = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : null;
      const dest = location.state?.from?.pathname || safeNext || afterLogin || '/dashboard';
      if (result && result.requires_2fa) {
        navigate('/login/verify', {
          state: {
            challenge_token: result.challenge_token,
            email_masked: result.email_masked,
            expires_in_seconds: result.expires_in_seconds,
            redirect_to: dest,
          },
          replace: true,
        });
        return;
      }
      navigate(dest, { replace: true });
    } catch (err) {
      // 0.48.26 - Not approved yet.
      if (err.status === 403 && err.message === 'pending_approval') setError(t('login.pending_approval'));
      else if (err.status === 423) setError(err.message || t('login.error_locked'));
      else if (err.status === 401) setError(t('login.error_invalid'));
      else setError(t('login.error_generic'));
    } finally {
      setLoading(false);
    }
  }, [loading, values, names, login, location, navigate, t]);

  const cardHref = cardPort
    ? `https://${window.location.hostname}:${cardPort}/api/auth/id-card/begin?next=${encodeURIComponent('/login/id-card')}`
    : '/login/id-card';

  const message = blockedMethod === 'id_card_only' ? (
    <>
      {t('login.use_id_card.message')}{' '}
      <Link to="/login/id-card">{t('login.use_id_card.cta')}</Link>
    </>
  ) : (error || null);

  const system = useMemo(() => ({
    kind: 'login',
    hidden: new Set([
      ...(message ? [] : ['login_message']),
      ...(idCardAvailable ? [] : ['login_id_card']),
    ]),
    messages: message ? { login_message: message } : {},
    busy: { login_submit: loading },
    hrefs: { login_id_card: cardHref, login_forgot: '/forgot-password' },
    onButton: (key, ev) => {
      if (key === 'login_submit') { ev.preventDefault(); submit(); return false; }
      if (key === 'login_id_card') {
        if (cardPort) return true;              // the card address: a full page load
        ev.preventDefault(); navigate('/login/id-card'); return false;
      }
      if (key === 'login_forgot') { ev.preventDefault(); navigate('/forgot-password'); return false; }
      return true;
    },
  }), [message, idCardAvailable, loading, cardHref, cardPort, submit, navigate]);

  if (failed) return <Login />;
  if (!page) return <main className="main" aria-busy="true" />;

  return (
    <main className="main">
      {/* A form, so Enter in a field signs in and a password manager
          recognises the page. */}
      <form noValidate onSubmit={(e) => { e.preventDefault(); submit(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target && e.target.tagName === 'INPUT') {
                e.preventDefault(); submit();
              }
            }}>
        <PageContentRenderer structure={page.structure} buttonTemplates={buttonTemplates}
                             rules={page.rules || []} settings={page.settings || null}
                             externalValues={values} onValueChange={setValues} system={system}
                             pageSlug={page.slug || ''} />
      </form>
    </main>
  );
}
